import {useEffect, useState} from "react";
import {fetchCurrentUser} from "../../lib/api";
import LoadingModal from "../Common/LoadingModal";
import CurrentUserContext from "../../contexts/CurrentUserContext";
import OpenWeatherMapContext from "../../contexts/OpenWeatherMapContext";
import SelectedFleetQueryParamLoader from "./SelectedFleetQueryParamLoader";
import { normalizeCurrentUser } from "../../utils/licenseAccess";
import { setAnalyticsUserId, clearAnalyticsUserId } from "../../lib/analytics";

const AuthProvider = ({ children, skipLoadingGate = false }) => {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [openWeatherMapApiKeys, setOpenWeatherMapApiKeys] = useState({
    accountApiKey: null,
    cityIdApiKey: null,
  });

  const logout = () => {
    document.documentElement.classList.remove("dark");
    localStorage.clear();
    setCurrentUser(null);
    setNotification(null);
    clearAnalyticsUserId();
  };

  const login = (user, nextNotification = null) => {
    setCurrentUser(normalizeCurrentUser(user));
    setNotification(nextNotification);
    setAnalyticsUserId(user?.unid);
  };

  const updateCurrentUserWithNormalization = (updates) => {
    setCurrentUser((prevUser) => {
      if (!prevUser) {
        return prevUser;
      }

      return normalizeCurrentUser({
        ...prevUser,
        ...updates,
      });
    });
  };

  const dismissNotification = () => {
    setNotification(null);
  };

  const unsetOpenWeatherMapApiKeys = () => {
    setOpenWeatherMapApiKeys({
      accountApiKey: null,
      cityIdApiKey: null,
    });
  }

  const applyUserResponse = (userResponse) => {
    setCurrentUser(normalizeCurrentUser(userResponse.data.user));
    setNotification(userResponse.data.notification || null);
    setOpenWeatherMapApiKeys({
      accountApiKey: userResponse.data.frontendSecrets?.openWeatherMapApiKey || null,
      cityIdApiKey: userResponse.data.frontendSecrets?.openWeatherMapCityIdApiKey || null,
    });
    setAnalyticsUserId(userResponse.data.user?.unid);
  };

  // will call on every 401 response
  useEffect(() => {
    window.addEventListener("unauthorized", logout);
    return () => window.removeEventListener("unauthorized", logout);
  }, []);

  useEffect(() => {
    const requireLicenseAgreement = () => {
      updateCurrentUserWithNormalization({ hasAcceptedLicenseAgreement: false });
    };
    window.addEventListener("licenseAgreementRequired", requireLicenseAgreement);
    return () =>
      window.removeEventListener("licenseAgreementRequired", requireLicenseAgreement);
  }, []);

  useEffect(() => {
    const isRefreshingRef = { current: false };
    const refreshCurrentUser = async () => {
      if (isRefreshingRef.current) {
        return;
      }
      isRefreshingRef.current = true;
      try {
        const userResponse = await fetchCurrentUser();
        if (userResponse.status === 200) {
          applyUserResponse(userResponse);
        }
      } catch (err) {
        console.error("Failed to refresh current user after billing limit event:", err);
      } finally {
        isRefreshingRef.current = false;
      }
    };
    window.addEventListener("billingLimitExceeded", refreshCurrentUser);
    return () =>
      window.removeEventListener("billingLimitExceeded", refreshCurrentUser);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        const userResponse = await fetchCurrentUser();
        if (userResponse.status === 200) {
          applyUserResponse(userResponse);
        } else {
          setCurrentUser(null);
          setNotification(null);
          setOpenWeatherMapApiKeys({
            accountApiKey: null,
            cityIdApiKey: null,
          });
          clearAnalyticsUserId();
          console.log("Unauthorized:", userResponse.message);
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        setCurrentUser(null);
        setNotification(null);
        setOpenWeatherMapApiKeys({
          accountApiKey: null,
          cityIdApiKey: null,
        });
        clearAnalyticsUserId();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading && !skipLoadingGate) {
    return <LoadingModal />
  }

  return (
    <CurrentUserContext.Provider value={{currentUser, notification, login, logout, updateCurrentUser: updateCurrentUserWithNormalization, dismissNotification}}>
      <OpenWeatherMapContext.Provider value={{
        apiKey: openWeatherMapApiKeys.accountApiKey,
        cityIdApiKey: openWeatherMapApiKeys.cityIdApiKey,
        setApiKeys: (apiKey, cityIdApiKey) => {
          setOpenWeatherMapApiKeys({
            accountApiKey: apiKey,
            cityIdApiKey: cityIdApiKey,
          });
        },
        unsetApiKeys: unsetOpenWeatherMapApiKeys,
      }}>
        <SelectedFleetQueryParamLoader currentUser={currentUser} skipLoadingGate={skipLoadingGate}>
          {children}
        </SelectedFleetQueryParamLoader>
      </OpenWeatherMapContext.Provider>
    </CurrentUserContext.Provider>
  );
};

export default AuthProvider;
